import { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import { ReservationLinkResult } from '@mews-commander/data-access';
import { WithAccountId } from '@mews-commander/types';
import { IconName, Stack, Tag } from '@optimus-web/core';
import { LocalizedCurrency, LocalizedEnum, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { LocalizedDateFromUtc, SpacedDash, TooltippedIcon, Dash } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { PaymentPlanDetailItem } from '../../views/PaymentPlanDetails/PaymentPlanDetailItem';
import { useGetAmountsForPaymentPlanSuspense, useGetPaymentPlanLocalizedDates } from './hooks';
import { CreatePaymentPlanDetailCard } from './CreatePaymentPlanDetailCard';
import { CreatePaymentPlanFormValues } from './schema';
import { getDefaultAmount } from './utils';

export const CreatePaymentPlanSummary = ({ reservation }: WithAccountId & { reservation: ReservationLinkResult }) => {
    const { getValues } = useFormContext<CreatePaymentPlanFormValues>();
    const { planName, paymentMethods = [], amount } = getValues();
    const { reservationStartUtc, reservationEndUtc, reservationNumber } = reservation;
    const {
        firstPaymentRequestExpirationDate,
        firstPaymentCoversStartDate,
        firstPaymentCoversEndDate,
        planStartDate,
        planEndDate,
    } = useGetPaymentPlanLocalizedDates(reservation);
    const { monthlyAmounts, averageMonthlyRate } = useGetAmountsForPaymentPlanSuspense(reservation);
    const planCurrency = getDefaultAmount(monthlyAmounts[0].amount).currency;

    return (
        <Stack spacing="200" vertical>
            <CreatePaymentPlanDetailCard title="CreatePaymentPlanFormStepLabelPlanDetails">
                <SummaryDetailItem description={TextKey.CreatePaymentPlanSummaryLabelPlanName} value={planName} />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelReservation}
                    value={<Tag variant="basicSubtle">{reservationNumber}</Tag>}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanFirstPaymentDetailsLabelReservationDates}
                    value={
                        <>
                            <LocalizedDateFromUtc utcString={reservationStartUtc} />
                            {SpacedDash}
                            <LocalizedDateFromUtc utcString={reservationEndUtc} />
                        </>
                    }
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanFirstPaymentDetailsLabelPlanDates}
                    value={`${planStartDate}${SpacedDash}${planEndDate}`}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelFrequency}
                    value={lt('PaymentPlanMonthlyFrequency', { dayOfMonthNumber: 1 })}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanFirstPaymentDetailsLabelMonthRate}
                    value={
                        averageMonthlyRate ? (
                            <LocalizedCurrency value={averageMonthlyRate.value} currency={planCurrency} />
                        ) : (
                            <Dash />
                        )
                    }
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelPaymentMethods}
                    value={
                        <Stack spacing="50">
                            {paymentMethods.map((method) => (
                                <Tag variant="basicSubtle" key={method}>
                                    <LocalizedEnum prefix="PaymentMethods" value={method} />
                                </Tag>
                            ))}
                        </Stack>
                    }
                />
            </CreatePaymentPlanDetailCard>

            <CreatePaymentPlanDetailCard title={TextKey.CreatePaymentPlanFormStepLabelFirstPayment}>
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelProcessDate}
                    value={
                        <>
                            <Tag variant="basicSubtle">{lt('CreatePaymentPlanSummaryProcessDateProcessNow')}</Tag>
                            {UnicodeEntity.NoBreakSpace}
                            {lt('CreatePaymentPlanSummaryProcessDateCoversPeriod', {
                                period: `${firstPaymentCoversStartDate}${SpacedDash}${firstPaymentCoversEndDate}`,
                            })}
                        </>
                    }
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelFirstPaymentProcessing}
                    value={lt('PaymentRequest')}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelPaymentRequestReason}
                    value={<Tag variant="basicSubtle">{lt('RecurringPayment')}</Tag>}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelPaymentRequestExpiration}
                    value={firstPaymentRequestExpirationDate ?? <Dash />}
                />
                <SummaryDetailItem
                    description={TextKey.CreatePaymentPlanSummaryLabelFirstPaymentAmount}
                    tooltippedIcon={
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={lt('CreatePaymentPlanSummaryTooltipFirstPaymentAmount')}
                        />
                    }
                    value={<LocalizedCurrency value={amount} currency={planCurrency} />}
                />
            </CreatePaymentPlanDetailCard>
        </Stack>
    );
};

const SummaryDetailItem = ({
    description,
    value,
    tooltippedIcon,
}: Omit<ComponentProps<typeof PaymentPlanDetailItem>, 'textStyle'>) => (
    <PaymentPlanDetailItem
        description={description}
        value={value}
        tooltippedIcon={tooltippedIcon}
        textStyle="bodyMedium"
    />
);
